// ---------------------------------------------------------------------------
// Cart drawer + checkout modal.
//
// Same markup and classes as the original design. The only behavioural change
// is the final step: instead of composing a WhatsApp message, the form posts a
// real order and the customer lands on a confirmation page.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api, ApiClientError } from '../../lib/apiClient.js';
import { useSite } from '../../context/SiteProvider.jsx';
import { useCart } from '../../context/CartProvider.jsx';
import { useToast } from '../../context/ToastProvider.jsx';

/** Traps Tab inside the panel and closes it on Escape. */
function useDialogBehaviour(isOpen, onClose) {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const node = ref.current;
    const previouslyFocused = document.activeElement;
    const selector = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const focusable = () => [...(node?.querySelectorAll(selector) ?? [])].filter((el) => el.offsetParent !== null);
    focusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, onClose]);

  return ref;
}

function CartLine({ line }) {
  const { t, pick, money } = useSite();
  const cart = useCart();

  return (
    <article className={`cart-item${line.available ? '' : ' is-unavailable'}`}>
      {line.imageUrl ? (
        <div className="cart-item-thumb">
          <img src={line.imageUrl} alt="" />
        </div>
      ) : null}
      <div>
        <h4>{pick(line.name)}</h4>
        <p>
          {pick(line.sizeLabel)} · {money(line.unitPrice)}
        </p>
        {!line.available ? <p className="cart-item-warning">{t('outOfStock')}</p> : null}
        <div className="cart-item-actions">
          <button
            type="button"
            aria-label={t('decrease')}
            onClick={() => cart.setQuantity(line.key, line.quantity - 1)}
          >
            −
          </button>
          <b>{line.quantity}</b>
          <button
            type="button"
            aria-label={t('increase')}
            disabled={line.quantity >= line.maxQuantity}
            onClick={() => cart.setQuantity(line.key, line.quantity + 1)}
          >
            +
          </button>
          <button type="button" className="cart-remove" onClick={() => cart.remove(line.key)}>
            {t('remove')}
          </button>
        </div>
      </div>
      <strong>{money(line.lineTotal)}</strong>
    </article>
  );
}

export function CartDrawer({ onCheckout }) {
  const { t, money } = useSite();
  const cart = useCart();
  const drawerRef = useDialogBehaviour(cart.isOpen, cart.close);

  return (
    <>
      <div
        id="cartOverlay"
        className={`cart-overlay${cart.isOpen ? ' open' : ''}`}
        onClick={cart.close}
        aria-hidden="true"
      />
      <aside
        id="cartDrawer"
        ref={drawerRef}
        className={`cart-drawer${cart.isOpen ? ' open' : ''}`}
        aria-hidden={!cart.isOpen}
        aria-label={t('shoppingCart')}
      >
        <div className="cart-top">
          <div>
            <p className="eyebrow">{t('yourOrder')}</p>
            <h2>{t('shoppingCart')}</h2>
          </div>
          <button id="cartCloseButton" type="button" aria-label={t('close')} onClick={cart.close}>
            ×
          </button>
        </div>

        <div id="cartItems" className="cart-items">
          {cart.lines.length ? (
            cart.lines.map((line) => <CartLine key={line.key} line={line} />)
          ) : (
            <div className="cart-empty-state">
              <p className="cart-empty">{t('cartEmpty')}</p>
              <small>{t('cartEmptyText')}</small>
            </div>
          )}
        </div>

        <div className="payment-notice">
          <span>✓</span>
          <div>
            <strong>{t('cashOnDelivery')}</strong>
            <small>{t('payWhenDelivered')}</small>
          </div>
        </div>

        <div className="cart-total">
          <span>{t('subtotal')}</span>
          <strong id="cartTotal">{money(cart.subtotal)}</strong>
        </div>

        <button
          id="customerInfoButton"
          className="button dark cart-main-button"
          type="button"
          disabled={cart.isEmpty || cart.hasUnavailable}
          onClick={() => {
            cart.close();
            onCheckout();
          }}
        >
          {t('checkout')}
        </button>
      </aside>
    </>
  );
}

const initialForm = {
  name: '',
  phone: '',
  email: '',
  governorateId: '',
  street: '',
  building: '',
  apartment: '',
  notes: '',
};

export function CustomerModal({ isOpen, onClose }) {
  const { t, pick, money, language } = useSite();
  const cart = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const cardRef = useDialogBehaviour(isOpen, onClose);

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data } = useQuery({
    queryKey: ['governorates'],
    queryFn: () => api.get('/governorates'),
    staleTime: 10 * 60 * 1000,
  });

  const governorates = data?.governorates ?? [];
  const selected = governorates.find((governorate) => governorate.id === form.governorateId) ?? null;
  const deliveryFee = selected?.deliveryFee ?? 0;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = t('fieldRequired');
    if (!/^(?:\+?20)?0?1[0125]\d{8}$/.test(form.phone.replace(/[\s()-]/g, ''))) next.phone = t('invalidPhone');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = t('invalidEmail');
    if (!form.governorateId) next.governorateId = t('errorInvalidGovernorate');
    if (form.street.trim().length < 2) next.street = t('fieldRequired');
    if (!form.building.trim()) next.building = t('fieldRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isSubmitting || !validate()) return;

    setIsSubmitting(true);
    try {
      const result = await api.post('/orders', {
        customerName: form.name.trim(),
        customerPhone: form.phone.trim(),
        customerEmail: form.email.trim(),
        governorateId: form.governorateId,
        street: form.street.trim(),
        buildingNumber: form.building.trim(),
        apartmentNumber: form.apartment.trim() || undefined,
        notes: form.notes.trim() || undefined,
        locale: language,
        items: cart.items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId ?? null,
          isCustom: Boolean(item.isCustom),
          customMl: item.customMl ?? null,
          quantity: item.quantity,
        })),
      });

      cart.clear();
      setForm(initialForm);
      onClose();
      navigate(`/order/${result.order.orderNumber}`, { state: { order: result.order, customer: form } });
    } catch (error) {
      const code = error instanceof ApiClientError ? error.code : 'UNKNOWN';
      if (code === 'OUT_OF_STOCK') {
        toast.show(t('errorOutOfStock', { item: error.details?.item ?? '' }), 'error');
      } else if (code === 'NETWORK_ERROR') {
        toast.show(t('errorNetwork'), 'error');
      } else {
        toast.show(t('errorGeneric'), 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      id="customerModal"
      className={`customer-modal${isOpen ? ' open' : ''}`}
      aria-hidden={!isOpen}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="customer-card" ref={cardRef} role="dialog" aria-modal="true" aria-label={t('customerInfo')}>
        <button
          id="customerCloseButton"
          className="customer-close"
          type="button"
          aria-label={t('close')}
          onClick={onClose}
        >
          ×
        </button>
        <p className="eyebrow">{t('checkoutDetails')}</p>
        <h2>{t('customerInfo')}</h2>

        <form id="customerForm" onSubmit={submit} noValidate>
          <div className="checkout-payment">
            <span>✓</span>
            <div>
              <strong>{t('paymentMethod')}</strong>
              <p>{t('cashOnDelivery')}</p>
            </div>
          </div>

          <label>
            <span>{t('fullName')}</span>
            <input value={form.name} onChange={update('name')} autoComplete="name" required />
            {errors.name ? <em className="field-error">{errors.name}</em> : null}
          </label>

          <label>
            <span>{t('customerPhone')}</span>
            <input type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" required />
            {errors.phone ? <em className="field-error">{errors.phone}</em> : null}
          </label>

          <label>
            <span>{t('customerEmail')}</span>
            <input type="email" value={form.email} onChange={update('email')} autoComplete="email" required />
            {errors.email ? <em className="field-error">{errors.email}</em> : null}
          </label>

          <label>
            <span>{t('governorate')}</span>
            <select value={form.governorateId} onChange={update('governorateId')} required>
              <option value="">{t('selectGovernorate')}</option>
              {governorates.map((governorate) => (
                <option key={governorate.id} value={governorate.id}>
                  {pick(governorate.name)}
                  {governorate.deliveryFee > 0 ? ` · ${money(governorate.deliveryFee)}` : ''}
                </option>
              ))}
            </select>
            {errors.governorateId ? <em className="field-error">{errors.governorateId}</em> : null}
          </label>

          <label className="field-wide">
            <span>{t('streetName')}</span>
            <input value={form.street} onChange={update('street')} autoComplete="address-line1" required />
            {errors.street ? <em className="field-error">{errors.street}</em> : null}
          </label>

          <label>
            <span>{t('buildingNumber')}</span>
            <input value={form.building} onChange={update('building')} required />
            {errors.building ? <em className="field-error">{errors.building}</em> : null}
          </label>

          <label>
            <span>{t('apartmentNumber')} <i>({t('optional')})</i></span>
            <input value={form.apartment} onChange={update('apartment')} />
          </label>

          <label className="field-wide">
            <span>{t('orderNotes')}</span>
            <textarea rows="2" value={form.notes} onChange={update('notes')} />
          </label>

          <div className="checkout-summary">
            <div>
              <span>{t('subtotal')}</span>
              <strong>{money(cart.subtotal)}</strong>
            </div>
            <div>
              <span>{t('deliveryFee')}</span>
              <strong>{selected ? (deliveryFee > 0 ? money(deliveryFee) : t('freeDelivery')) : '—'}</strong>
            </div>
            <div className="checkout-summary-total">
              <span>{t('orderTotal')}</span>
              <strong>{money(cart.subtotal + deliveryFee)}</strong>
            </div>
          </div>

          <p className="privacy-note">{t('orderPrivacyNote')}</p>

          <button className="button dark submit-order" type="submit" disabled={isSubmitting || cart.isEmpty}>
            <span>{isSubmitting ? t('placingOrder') : t('placeOrder')}</span>
            <span>↗</span>
          </button>
        </form>
      </div>
    </div>
  );
}
